import { FC } from 'react';

import { Card, SimpleGrid, Skeleton, Stack } from '@mantine/core';

import { CompanyProfile } from 'models';

import { IncompleteProfile } from 'components/company-profile/components/IncompleteProfile';
import { RelatedCategories } from 'components/company-profile/components/RelatedCategories';
import { useCurrentProfile } from 'components/company-profile/hooks/use-current-profile';
import { useShowCompanyInfo } from 'components/company-profile/hooks/use-show-company-info';
import { About } from 'components/company-profile/sections/About';
import { CaseStudies } from 'components/company-profile/sections/CaseStudies';
import { Collateral } from 'components/company-profile/sections/Collateral';
import { Contact } from 'components/company-profile/sections/Contact';
import { ContactPeople } from 'components/company-profile/sections/ContactPeople';
import { Memberships } from 'components/company-profile/sections/Memberships';
import { Partners } from 'components/company-profile/sections/Partners';
import { ProductSeries } from 'components/company-profile/sections/ProductSeries';
import { Products } from 'components/company-profile/sections/Products';
import { ReferenceDesigns } from 'components/company-profile/sections/ReferenceDesigns';
import { Services } from 'components/company-profile/sections/Services';

import cx from './styles.module.scss';

const Home: FC<{ company: CompanyProfile }> = ({ company }) => {
    const {
        showAbout,
        showHighlights,
        showReferenceDesigns,
        showProducts,
        showContact,
        showPromos,
        showContactPeople,
        showCaseStudies,
        showPartners,
        showProductSeries,
        showCollateral,
        showServiceInfo,
        shownLocations,
    } = useShowCompanyInfo(company);

    const isEmpty =
        !showAbout &&
        !showHighlights &&
        !showContactPeople &&
        !showPromos &&
        !showPartners &&
        !showProducts &&
        !showReferenceDesigns &&
        !showCaseStudies &&
        // Socials alone still leave the page empty
        !(showContact && shownLocations.length) &&
        !showProductSeries &&
        !showCollateral &&
        !showServiceInfo;

    return (
        <Stack className={cx.root}>
            {/* {showHighlights && <Highlights company={company} highlights={company.highlights} />} */}

            {/* {showPromos && <Stubs isCarousel field="promos" company={company} stubs={company.promos} />} */}

            <IncompleteProfile company={company} isEmpty={isEmpty} />

            <About company={company} />

            {showCollateral && <Collateral company={company} />}

            {showProductSeries && <ProductSeries company={company} title="Product Series" />}

            {showProducts && <Products isCarousel company={company} />}

            {showServiceInfo && <Services company={company} />}

            {showCaseStudies && <CaseStudies company={company} />}

            {showReferenceDesigns && <ReferenceDesigns isCarousel company={company} />}

            {showPartners && <Partners company={company} partners={company.partners} />}

            {showContactPeople && <ContactPeople company={company} contacts={company.contactPeople} />}

            {showContact && <Contact company={company} />}

            <Memberships company={company} />

            <RelatedCategories company={company} />
        </Stack>
    );
};

const WrappedHome = () => {
    const company = useCurrentProfile();

    if (!company) {
        return (
            <Card classNames={cx}>
                <Stack gap={8}>
                    <Skeleton height={8} radius="xl" />
                    <Skeleton height={8} mt={6} radius="xl" />
                    <Skeleton height={8} mt={6} width="70%" radius="xl" />
                </Stack>
                <SimpleGrid cols={3} h={300}>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                </SimpleGrid>
            </Card>
        );
    }

    return <Home company={company} />;
};

export { WrappedHome as Home };
